// Privacy Policy page (/privacy-policy). Readable without accepting the Terms: TermsGate skips this
// path, because platforms that review our apps (Pinterest, Google) link here and a privacy policy must
// be readable before anyone agrees to anything. Clause typography is shared with the Terms.
import "./TermsContent.css";
import "./PrivacyPolicy.css";

export const PRIVACY_PATH = "/privacy-policy";
export const PRIVACY_LAST_UPDATED = "16 October 2026";

const sections = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <p>
        This website and the tools described below are run by Anushthan Idea
        Labs ("Anushthan Idea Labs", "we", "us" or "our"). This policy explains
        what personal information we collect, why, where it is kept, who we
        share it with and the choices you have. It applies to this website
        (anushthangroups.com), our client SEO console (seo.anushthangroups.com)
        and the apps we connect to third-party platforms on our own accounts.
      </p>
    ),
  },
  {
    id: "website",
    title: "Information you give us on this website",
    body: (
      <>
        <p>
          When you use our contact form we collect your name, email address,
          phone number, your message and the name of any file you attach. We
          use this only to reply to your enquiry and to discuss the work you
          asked about. It is stored in Google Firebase (Cloud Firestore).
        </p>
        <p>
          When you accept our Terms and Conditions, your browser remembers that
          choice and its date in local storage on your own device; it is not
          sent to us. This website does not use advertising or analytics
          trackers.
        </p>
      </>
    ),
  },
  {
    id: "clients",
    title: "Information we handle for our clients",
    body: (
      <>
        <p>
          For clients who engage us for search engine optimisation, our SEO
          console works with: the client's contact name and email address; the
          public pages of the client's website, which we read to audit them;
          keyword and search ranking data for the client's website; and, only
          when the client grants our service account access, data from the
          client's Google Search Console and Google Analytics properties (for
          example search clicks, impressions, indexing status and organic
          visits). We do not collect information about the visitors to a
          client's website beyond the aggregated figures those Google tools
          report.
        </p>
        <p>
          This information is stored on Amazon Web Services in the Mumbai
          (ap-south-1) region and is used only to deliver the agreed services,
          such as audits, proposals, monthly reports and the emails that send
          them. Only our staff can sign in to the console.
        </p>
      </>
    ),
  },
  {
    id: "platform-apps",
    title: "Apps connected to third-party platforms",
    body: (
      <>
        <p>
          To publish articles and share links that we write as part of our
          services, we register apps with platforms such as Pinterest, Tumblr,
          Blogger, Bluesky, WordPress.com, Medium and the Internet Archive.
          Each app is connected only to Anushthan Idea Labs' own account on that
          platform.
        </p>
        <ul>
          <li>
            <strong>What the apps do:</strong> create posts, pins and bookmarks
            on our own accounts, and read our own account's boards or blogs so
            they know where to post.
          </li>
          <li>
            <strong>What they do not do:</strong> they do not sign in other
            people, and they do not read, collect or store information about
            other users of those platforms or their content.
          </li>
          <li>
            <strong>Access tokens:</strong> the keys that let an app post to
            our account are stored encrypted in AWS Systems Manager Parameter
            Store and used only by our publishing service. We can withdraw them
            at any time from the platform's settings.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "service-providers",
    title: "Service providers we use",
    body: (
      <>
        <p>
          We share information only with providers that help us run the
          services above, and only what each needs:
        </p>
        <ul>
          <li>
            <strong>Hosting and storage:</strong> Hostinger (this website),
            Google Firebase (contact form) and Amazon Web Services (SEO console,
            reports and email delivery through Amazon SES).
          </li>
          <li>
            <strong>Search data:</strong> DataForSEO (keyword volumes and
            rankings) and Google (Search Console, Analytics and PageSpeed
            Insights).
          </li>
          <li>
            <strong>AI writing and analysis:</strong> OpenAI and Anthropic
            process website text, keywords and briefs to help draft audits,
            proposals and articles. We do not send them contact form enquiries.
          </li>
        </ul>
        <p>
          We do not sell personal information, and we do not share it for
          advertising. We may disclose information if the law requires it.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep information",
    body: (
      <p>
        We keep contact form enquiries for as long as we need them to respond
        and follow up, and client information for the length of the engagement
        and afterwards for as long as we need it for our records, reporting
        history or legal obligations. You can ask us to delete your information
        at any time (see below).
      </p>
    ),
  },
  {
    id: "security",
    title: "Security",
    body: (
      <p>
        Access to our systems is limited to our staff. Credentials are stored
        encrypted, and data travels over encrypted (HTTPS) connections. No
        method of storage or transmission is completely secure, but we work to
        protect the information we hold.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "Your choices and rights",
    body: (
      <>
        <p>
          You can ask us to tell you what personal information we hold about
          you, to correct it, or to delete it, and you can withdraw any consent
          you have given. Clients can remove our access to their Google Search
          Console or Analytics at any time from those tools' user settings.
          We will respond to requests within a reasonable time and in line with
          applicable law, including India's Digital Personal Data Protection
          Act, 2023.
        </p>
        <p>
          Our services are meant for businesses and are not directed at
          children.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this policy when our services or the law change. The
        "Last updated" date above shows when it last changed.
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  return (
    <main className="privacy-page">
      <div className="container">
        <header className="privacy-header">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-updated">Last updated: {PRIVACY_LAST_UPDATED}</p>
        </header>
        <div className="terms-clause privacy-body">
          {sections.map((section, index) => (
            <section key={section.id} id={section.id} className="privacy-clause">
              <h2>
                <span className="privacy-number">{index + 1}.</span>
                {section.title}
              </h2>
              {section.body}
            </section>
          ))}
          <section id="contact" className="privacy-clause">
            <h2>
              <span className="privacy-number">{sections.length + 1}.</span>
              Contact us
            </h2>
            <p>
              For any question about this policy or to make a request about your
              information, write to us:
            </p>
            <div className="terms-contact-card">
              <p>
                <strong>Anushthan Idea Labs</strong>
              </p>
              <p>
                Email:{" "}
                <a href="mailto:anushthanidealabs@gmail.com">
                  anushthanidealabs@gmail.com
                </a>
              </p>
              <p className="mb-0">
                Or write to us through our <a href="/contact-us">contact form</a>.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
